import { useState } from 'react';
import { Drawer, Pagination, Box, Grid } from 'components/ui';
import { useQuery } from '@apollo/client/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FilterProvider, useFilter, initInspectionSubjectFilterValues } from 'contexts/filter';
import { DividerContainer } from 'components/styles_components/container';
import { useError } from 'contexts/error';
import { TitleTypography, SubtitleTypography } from 'components/styles_components/typography';
import { usePaginate } from 'contexts/paginate';
import FilterForm from 'components/inspection_subject/filter_form';
import FilterTags from 'components/filter/tags';
import FlexibleInput from 'components/flexible_input';
import CreateButton from 'components/inspection_subject/button/create';
import FilterButton from 'components/inspection_subject/button/filter';
import InspectionSubjectQuery from 'queries/inspection_subject/index';
import Index from 'components/inspection_subject/index';

const IndexContainer = ({ placeId, showTitle = true }) => {
  const intl = useIntl();
  const { page, onPageChange } = usePaginate();
  const { inspectionSubjectsFilter, setInspectionSubjectsFilter } = useFilter();
  const setError = useError();
  const [filterVisible, setFilterVisible] = useState(false);
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState('name_asc');
  const {
    data = {
      space: { overInspectionSubjectsLimit: false, adminRole: true },
      inspectionSubjects: { inspectionSubjects: [], inspectionSubjectsTotal: 0 },
    },
    loading,
  } = useQuery(InspectionSubjectQuery, {
    variables: {
      ...inspectionSubjectsFilter,
      search: search,
      page: page,
      placeId: placeId ?? inspectionSubjectsFilter.placeId,
      sortBy: sortBy,
    },
    cachePolicy: 'cache-and-network',
    onError: () => {
      setError('error.inspection_subject.index');
    },
  });

  return (
    <FilterProvider>
      {showTitle && (
        <>
          <TitleTypography variant="h1">
            <FormattedMessage id="navbar.inspection_subjects" />
          </TitleTypography>
          <SubtitleTypography variant="h2">
            <FormattedMessage
              id="inspection_subject.total"
              values={{ count: data.inspectionSubjects.inspectionSubjectsTotal ?? 0 }}
            />
          </SubtitleTypography>
        </>
      )}
      {filterVisible && (
        <Drawer
          anchor="right"
          open={filterVisible}
          onClose={() => {
            setFilterVisible(false);
          }}
        >
          <FilterForm
            initialValues={inspectionSubjectsFilter}
            defaultValues={initInspectionSubjectFilterValues}
            placeId={placeId}
            onSubmit={(values) => {
              onPageChange(1);
              setFilterVisible(false);
              setInspectionSubjectsFilter(values);
            }}
            onClose={() => {
              setFilterVisible(false);
            }}
          />
        </Drawer>
      )}
      <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
        <Grid size={{ xs: 12, sm: 'auto' }} sx={{ flexGrow: { sm: 1 }, minWidth: 0 }}>
          <FlexibleInput
            placeholder={intl.formatMessage({ id: 'flexible_filter.inspection_subject' })}
            value={search}
            onChange={setSearch}
          />
        </Grid>
        <Grid size="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <FilterButton
            onClick={() => setFilterVisible(!filterVisible)}
            initInspectionSubjectFilterValues={initInspectionSubjectFilterValues}
            inspectionSubjectsFilter={inspectionSubjectsFilter}
          />
          {data.space.adminRole && (
            <CreateButton
              overInspectionSubjectsLimit={data.space.overInspectionSubjectsLimit}
              placeId={placeId}
            />
          )}
        </Grid>
      </Grid>
      <FilterTags
        filterValues={
          placeId ? { ...inspectionSubjectsFilter, placeId: null } : inspectionSubjectsFilter
        }
        defaultValues={initInspectionSubjectFilterValues}
        onFilterChange={setInspectionSubjectsFilter}
      />
      <Index
        loading={loading}
        inspectionSubjects={data.inspectionSubjects}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      <Box display="flex" justifyContent="center">
        {data.inspectionSubjects.inspectionSubjectsTotalPages > 1 && (
          <Pagination
            count={data.inspectionSubjects.inspectionSubjectsTotalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
      <DividerContainer />
    </FilterProvider>
  );
};

export default IndexContainer;
